import React, { useState, useEffect } from 'react';
import { IoClose } from 'react-icons/io5';
import { FaInfoCircle, FaExclamationTriangle, FaGift, FaFire, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { useWorkspace } from '../../context/WorkspaceContext';

const AnnouncementBanner = ({
    rotationInterval = 30000, // 30 seconds in milliseconds
    showNavigation = true, // Show left/right navigation arrows
    showCloseButton = true,
    bannerId = "rotating-banner", // Unique ID for the rotating banner
    dismissDuration = 14 // Days to hide banner after dismissal
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const { firestoreUser } = useWorkspace();

    useEffect(() => {
        // Check if banner was dismissed within the specified duration
        const dismissedTime = localStorage.getItem(`banner-dismissed-${bannerId}`);
        const currentTime = new Date().getTime();
        const durationInMs = dismissDuration * 24 * 60 * 60 * 1000; // Convert days to milliseconds

        // Check if user is new (less than 7 days old)
        let shouldShow = true;
        if (firestoreUser && firestoreUser.createdAt) {
            const accountCreationTime = new Date(firestoreUser.createdAt).getTime();
            const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
            const accountAge = currentTime - accountCreationTime;
            
            if (accountAge < sevenDaysInMs) {
                shouldShow = false; // Don't show to new users
            }
        }

        if (shouldShow && (!dismissedTime || (currentTime - parseInt(dismissedTime)) > durationInMs)) {
            setIsVisible(true);
        }
    }, [bannerId, dismissDuration, firestoreUser]);

    // All announcements configuration - you can modify these without touching WorkspaceRoutes
    const announcements = [
        {
            message: "🎉 Become an affiliate and earn 20% on every sale! Have over 10k followers? Contact us for a custom deal.",
            type: "promo",
            actionButton: {
                text: "Claim Offer",
                onClick: () => window.open('/dashboard/affiliate', '_blank'),
                className: "bg-white text-orange-600 hover:bg-gray-100"
            }
        },
        {
            message: "💎 Save 20% on yearly subscription! Best value for power users",
            type: "success",
            actionButton: {
                text: "View Plans",
                onClick: () => window.open('/dashboard/settings/subscription', '_blank'),
                className: "bg-white text-green-600 hover:bg-gray-100"
            }
        },
        {
            message: "🔥 New AI features available! Try our advanced content generator",
            type: "success",
            actionButton: {
                text: "Try AI Tools",
                onClick: () => window.open('/dashboard/tools/ai-generator', '_blank'),
                className: "bg-white text-green-600 hover:bg-gray-100"
            }
        }
    ];

    // Default styles based on type
    const getDefaultStyles = (type) => {
        switch (type) {
            case "info":
                return {
                    bg: "bg-blue-500",
                    text: "text-white",
                    icon: <FaInfoCircle size={16} />
                };
            case "warning":
                return {
                    bg: "bg-yellow-500",
                    text: "text-white",
                    icon: <FaExclamationTriangle size={16} />
                };
            case "success":
                return {
                    bg: "bg-green-500",
                    text: "text-white",
                    icon: <FaGift size={16} />
                };
            case "promo":
                return {
                    bg: "bg-gradient-to-r from-orange-500 to-red-500",
                    text: "text-white",
                    icon: <FaFire size={16} />
                };
            default:
                return {
                    bg: "bg-gradient-to-r from-blue-500 to-purple-600",
                    text: "text-white",
                    icon: null
                };
        }
    };

    // Auto-rotate announcements
    useEffect(() => {
        if (!isVisible || announcements.length <= 1) return;

        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) =>
                prevIndex === announcements.length - 1 ? 0 : prevIndex + 1
            );
        }, rotationInterval);

        return () => clearInterval(interval);
    }, [isVisible, rotationInterval, announcements.length]);

    const handleClose = () => {
        setIsVisible(false);
        // Store the current time when banner was dismissed
        localStorage.setItem(`banner-dismissed-${bannerId}`, new Date().getTime().toString());
    };

    const handlePrevious = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? announcements.length - 1 : prevIndex - 1
        );
    };

    const handleNext = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === announcements.length - 1 ? 0 : prevIndex + 1
        );
    };

    if (!isVisible) {
        return null;
    }

    const currentAnnouncement = announcements[currentIndex];
    const defaultStyles = getDefaultStyles(currentAnnouncement.type);
    const bgColor = currentAnnouncement.backgroundColor || defaultStyles.bg;
    const txtColor = currentAnnouncement.textColor || defaultStyles.text;
    const icon = currentAnnouncement.showIcon !== false ? defaultStyles.icon : null;

    return (
        <div className={`${bgColor} ${txtColor} py-3 px-4 relative z-20 shadow-sm`}>
            <div className="container mx-auto flex items-center justify-center text-center">
                {/* Navigation arrows */}
                {showNavigation && announcements.length > 1 && (
                    <button
                        onClick={handlePrevious}
                        className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors duration-200 flex-shrink-0 mr-2"
                        aria-label="Previous announcement"
                    >
                        <FaChevronLeft size={16} />
                    </button>
                )}

                {/* Main content */}
                <div className="flex items-center flex-1 justify-center">
                    {icon && (
                        <span className="mr-2 flex-shrink-0">
                            {icon}
                        </span>
                    )}
                    <div className="text-sm md:text-base font-medium">
                        {currentAnnouncement.message}
                    </div>
                </div>

                <div className="flex items-center ml-4">
                    {/* Action button */}
                    {currentAnnouncement.actionButton && (
                        <button
                            onClick={currentAnnouncement.actionButton.onClick}
                            className={`mr-3 px-4 py-1 text-xs font-medium rounded-full bg-white bg-opacity-20 hover:bg-opacity-30 transition-colors duration-200 ${currentAnnouncement.actionButton.className || ''}`}
                        >
                            {currentAnnouncement.actionButton.text}
                        </button>
                    )}

                    {/* Navigation arrows */}
                    {showNavigation && announcements.length > 1 && (
                        <button
                            onClick={handleNext}
                            className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors duration-200 flex-shrink-0 mr-2"
                            aria-label="Next announcement"
                        >
                            <FaChevronRight size={16} />
                        </button>
                    )}

                    {/* Close button */}
                    {showCloseButton && (
                        <button
                            onClick={handleClose}
                            className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors duration-200 flex-shrink-0"
                            aria-label="Close announcement"
                        >
                            <IoClose size={18} />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AnnouncementBanner;